import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { MediaPicker } from "@/components/ui/media-picker";
import { TabBar } from "@/components/ui/tab-bar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { CaretUp, CaretDown, Plus, CircleNotch, Trash, Star } from "@phosphor-icons/react";
import type { Testimonial, TestimonialRole, TestimonialPlacement } from "@/types/testimonials";
import { TESTIMONIAL_QUOTE_MAX, TESTIMONIAL_FULL_QUOTE_MAX } from "@/schemas/testimonials";

function CharCount({ value, max }: { value: string | undefined; max: number }) {
  const length = value?.length ?? 0;
  return (
    <p className={`text-right text-xs ${length > max ? "text-rose-600" : "text-muted-foreground"}`}>
      {length}/{max}
    </p>
  );
}

const ROLE_LABELS: Record<TestimonialRole, string> = {
  client: "Klijent",
  therapist: "Terapeut",
  hr: "HR Manager",
};

const PLACEMENT_TABS: Array<{ value: TestimonialPlacement; label: string }> = [
  { value: "home", label: "Početna" },
  { value: "therapists", label: "Za terapeute" },
];

const PLACEMENT_HINTS: Record<TestimonialPlacement, string> = {
  home: "Sekcija „Šta kažu naši klijenti“ na početnoj stranici.",
  therapists: "Carousel recenzija terapeuta na stranici /za-terapeute.",
};

function invalidate(qc: ReturnType<typeof useQueryClient>, placement: TestimonialPlacement) {
  qc.invalidateQueries({ queryKey: ["admin", "testimonials", placement] });
  qc.invalidateQueries({ queryKey: ["homepage-testimonials", "public", placement] });
}

export function TestimonialsSection() {
  const qc = useQueryClient();
  const [placement, setPlacement] = useState<TestimonialPlacement>("home");
  const [form, setForm] = useState<Partial<Testimonial> | null>(null);
  const isTherapists = placement === "therapists";

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "testimonials", placement],
    queryFn: async () => {
      const res = await fetch(`/api/admin/testimonials?placement=${placement}`);
      await throwIfNotOk(res, "Greška pri učitavanju testimoniala");
      return res.json() as Promise<{ testimonials: Testimonial[] }>;
    },
  });
  const testimonials = data?.testimonials ?? [];

  const save = useMutation({
    mutationFn: async (t: Partial<Testimonial>) => {
      const isEdit = Boolean(t.id);
      const res = await fetch(
        isEdit ? `/api/admin/testimonials/${t.id}` : "/api/admin/testimonials",
        {
          method: isEdit ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            // Placement is fixed at creation — sortOrder is numbered per list.
            ...(isEdit ? {} : { placement }),
            name: t.name,
            role: t.role,
            roleBs: t.roleBs ?? "",
            roleEn: t.roleEn ?? "",
            quoteBs: t.quoteBs,
            quoteEn: t.quoteEn,
            fullQuoteBs: t.fullQuoteBs ?? "",
            fullQuoteEn: t.fullQuoteEn ?? "",
            avatarUrl: t.avatarUrl ?? "",
            rating: t.rating ?? 5,
            isActive: t.isActive ?? true,
          }),
        },
      );
      await throwIfNotOk(res, "Greška pri spremanju testimoniala");
      return res.json();
    },
    onSuccess: () => {
      invalidate(qc, placement);
      setForm(null);
      toast.success("Spremljeno");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/admin/testimonials/${id}`, { method: "DELETE" });
      await throwIfNotOk(res, "Greška pri brisanju");
    },
    onSuccess: () => {
      invalidate(qc, placement);
      setForm(null);
      toast.success("Obrisano");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const move = useMutation({
    mutationFn: async ({ id, direction }: { id: string; direction: "up" | "down" }) => {
      const res = await fetch(`/api/admin/testimonials/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ direction }),
      });
      await throwIfNotOk(res, "Greška pri promjeni redoslijeda");
    },
    onSuccess: () => invalidate(qc, placement),
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <h3
            className="text-base font-semibold text-foreground"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Testimoniali
          </h3>
          <p className="mt-0.5 text-sm text-muted-foreground">{PLACEMENT_HINTS[placement]}</p>
        </div>
        <Button
          onClick={() =>
            setForm({
              role: isTherapists ? "therapist" : "client",
              rating: 5,
              isActive: true,
            })
          }
        >
          <Plus className="size-4" />
          Dodaj
        </Button>
      </div>

      <TabBar
        tabs={PLACEMENT_TABS}
        value={placement}
        onChange={(next) => {
          setPlacement(next as TestimonialPlacement);
          setForm(null);
        }}
        ariaLabel="Gdje se testimoniali prikazuju"
        mobileMode="select"
      />

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-xl bg-muted/50" />
          ))}
        </div>
      ) : testimonials.length === 0 ? (
        <p className="rounded-xl border border-dashed py-10 text-center text-sm text-muted-foreground">
          Nema testimoniala. Dodajte prvi — sekcija se ne prikazuje dok nema aktivnih.
        </p>
      ) : (
        <ul className="divide-y divide-border/50 rounded-xl border border-border/60 bg-card shadow-sm">
          {testimonials.map((t, index) => (
            <li key={t.id} className="flex items-center gap-3 p-3">
              {!isTherapists && t.avatarUrl && (
                <img
                  src={t.avatarUrl}
                  alt=""
                  className="size-12 shrink-0 rounded-full object-cover"
                  loading="lazy"
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-foreground">{t.name}</span>
                  <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                    {isTherapists ? t.roleBs || ROLE_LABELS[t.role] : ROLE_LABELS[t.role]}
                  </span>
                  {!isTherapists && (
                    <span className="inline-flex shrink-0 items-center text-amber-500">
                      {Array.from({ length: t.rating }, (_, i) => (
                        <Star key={i} weight="fill" className="size-3" />
                      ))}
                    </span>
                  )}
                  {!t.isActive && (
                    <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] text-amber-700">
                      Skriveno
                    </span>
                  )}
                </div>
                <p className="mt-0.5 truncate text-sm text-muted-foreground">{t.quoteBs}</p>
              </div>
              <div className="flex shrink-0 items-center text-muted-foreground/60">
                <button
                  type="button"
                  onClick={() => move.mutate({ id: t.id, direction: "up" })}
                  disabled={index === 0}
                  className="rounded p-1 transition-colors hover:text-foreground disabled:opacity-30"
                  aria-label="Pomjeri gore"
                >
                  <CaretUp className="size-4" />
                </button>
                <button
                  type="button"
                  onClick={() => move.mutate({ id: t.id, direction: "down" })}
                  disabled={index >= testimonials.length - 1}
                  className="rounded p-1 transition-colors hover:text-foreground disabled:opacity-30"
                  aria-label="Pomjeri dole"
                >
                  <CaretDown className="size-4" />
                </button>
                <Button variant="outline" size="sm" className="ml-2" onClick={() => setForm(t)}>
                  Uredi
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {form && (
        <TestimonialFormDialog
          form={form}
          placement={placement}
          isSaving={save.isPending}
          isDeleting={remove.isPending}
          onChange={setForm}
          onSave={() => save.mutate(form)}
          onDelete={form.id ? () => remove.mutate(form.id!) : undefined}
          onClose={() => setForm(null)}
        />
      )}
    </div>
  );
}

function TestimonialFormDialog({
  form,
  placement,
  isSaving,
  isDeleting,
  onChange,
  onSave,
  onDelete,
  onClose,
}: {
  form: Partial<Testimonial>;
  placement: TestimonialPlacement;
  isSaving: boolean;
  isDeleting: boolean;
  onChange: (next: Partial<Testimonial>) => void;
  onSave: () => void;
  onDelete?: () => void;
  onClose: () => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isEdit = Boolean(form.id);
  const isTherapists = placement === "therapists";
  // Avatar is optional: neither placement renders it and seeded rows have "".
  const valid =
    Boolean(form.name?.trim()) &&
    Boolean(form.quoteBs?.trim()) &&
    Boolean(form.quoteEn?.trim()) &&
    (form.quoteBs?.length ?? 0) <= TESTIMONIAL_QUOTE_MAX &&
    (form.quoteEn?.length ?? 0) <= TESTIMONIAL_QUOTE_MAX;

  return (
    <Dialog open onClose={onClose} className={isTherapists ? "sm:max-w-3xl" : undefined}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (valid && !isSaving) onSave();
        }}
      >
        <DialogHeader onClose={onClose}>
          <AdminDialogHeading
            icon={Star}
            title={isEdit ? "Uredi testimonial" : "Novi testimonial"}
            description={
              isTherapists
                ? "Recenzija terapeuta na stranici /za-terapeute."
                : "Uredite javni citat, avatar i status prikaza."
            }
          />
        </DialogHeader>

        <DialogBody>
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Ime</Label>
                <Input
                  value={form.name ?? ""}
                  onChange={(e) => onChange({ ...form, name: e.target.value })}
                  placeholder={isTherapists ? "npr. Vildana Efendić" : "npr. Adna M."}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Uloga</Label>
                <Select
                  value={form.role ?? (isTherapists ? "therapist" : "client")}
                  onValueChange={(v) => onChange({ ...form, role: v as TestimonialRole })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(ROLE_LABELS) as TestimonialRole[]).map((r) => (
                      <SelectItem key={r} value={r}>
                        {ROLE_LABELS[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {isTherapists ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Potpis ispod imena (bosanski)</Label>
                  <Input
                    value={form.roleBs ?? ""}
                    onChange={(e) => onChange({ ...form, roleBs: e.target.value })}
                    placeholder="npr. Terapeutkinja na Psihotronu"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Potpis ispod imena (engleski)</Label>
                  <Input
                    value={form.roleEn ?? ""}
                    onChange={(e) => onChange({ ...form, roleEn: e.target.value })}
                    placeholder="npr. Therapist on Psihotron"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Slika (avatar, nije obavezno)</Label>
                <MediaPicker
                  value={form.avatarUrl ?? ""}
                  onChange={(url) => onChange({ ...form, avatarUrl: url })}
                  folder="testimonials"
                  accept="image/jpeg,image/png,image/webp"
                  hint="JPG, PNG ili WebP"
                  previewHeight="h-28"
                />
              </div>
            )}

            <div className="space-y-1.5">
              <Label>{isTherapists ? "Kratki citat (bosanski)" : "Citat (bosanski)"}</Label>
              <Textarea
                value={form.quoteBs ?? ""}
                onChange={(e) => onChange({ ...form, quoteBs: e.target.value })}
                rows={3}
                maxLength={TESTIMONIAL_QUOTE_MAX}
                className="resize-none"
              />
              <CharCount value={form.quoteBs} max={TESTIMONIAL_QUOTE_MAX} />
            </div>
            <div className="space-y-1.5">
              <Label>{isTherapists ? "Kratki citat (engleski)" : "Citat (engleski)"}</Label>
              <Textarea
                value={form.quoteEn ?? ""}
                onChange={(e) => onChange({ ...form, quoteEn: e.target.value })}
                rows={3}
                maxLength={TESTIMONIAL_QUOTE_MAX}
                className="resize-none"
              />
              <CharCount value={form.quoteEn} max={TESTIMONIAL_QUOTE_MAX} />
            </div>

            {isTherapists && (
              <>
                <div className="space-y-1.5">
                  <Label>Cijela recenzija (bosanski)</Label>
                  <Textarea
                    value={form.fullQuoteBs ?? ""}
                    onChange={(e) => onChange({ ...form, fullQuoteBs: e.target.value })}
                    rows={8}
                    maxLength={TESTIMONIAL_FULL_QUOTE_MAX}
                  />
                  <p className="text-xs text-muted-foreground">
                    Prikazuje se u dijalogu „Pročitaj čitavu recenziju“. Ostavite prazno i dugme se ne prikazuje.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label>Cijela recenzija (engleski)</Label>
                  <Textarea
                    value={form.fullQuoteEn ?? ""}
                    onChange={(e) => onChange({ ...form, fullQuoteEn: e.target.value })}
                    rows={8}
                    maxLength={TESTIMONIAL_FULL_QUOTE_MAX}
                  />
                </div>
              </>
            )}

            <div className="grid items-end gap-4 sm:grid-cols-2">
              {!isTherapists && (
                <div className="space-y-1.5">
                  <Label>Ocjena</Label>
                  <Select
                    value={String(form.rating ?? 5)}
                    onValueChange={(v) => onChange({ ...form, rating: Number(v) })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 4, 3, 2, 1].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {"★".repeat(n)} ({n})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <label className="flex h-9 cursor-pointer items-center gap-2.5 rounded-lg border bg-muted/10 px-3">
                <Checkbox
                  checked={form.isActive ?? true}
                  onCheckedChange={(c) => onChange({ ...form, isActive: c === true })}
                />
                <span className="text-sm font-medium">
                  {isTherapists ? "Prikaži na /za-terapeute" : "Prikaži na početnoj"}
                </span>
              </label>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          {isEdit && onDelete && (
            confirmDelete ? (
              <div className="flex w-full items-center justify-between gap-2 sm:mr-auto">
                <p className="text-sm font-medium text-rose-600">Obrisati?</p>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setConfirmDelete(false)}>
                    Odustani
                  </Button>
                  <Button type="button" variant="destructive" size="sm" disabled={isDeleting} onClick={onDelete}>
                    {isDeleting && <CircleNotch className="size-4 animate-spin" />}
                    Obriši
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-rose-600 sm:mr-auto"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash className="size-4" />
                Obriši
              </Button>
            )
          )}
          <Button type="button" variant="outline" onClick={onClose}>
            Zatvori
          </Button>
          <Button type="submit" disabled={!valid || isSaving}>
            {isSaving && <CircleNotch className="size-4 animate-spin" />}
            Spremi
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
